import firebase from 'firebase/compat/app'
import { Cohort, Currency, PaymentSystem } from 'nove8/constants/root-constants'
import { TimeInterval } from 'root-constants'
import { PageId } from 'src/constants/pages'
import { TAnswers } from './common.model'

export interface IUpdateLeadRequest {
  email?: string | null
  extra?: Record<string, unknown>
  firebaseToken?: string | null
}

interface ICreateCohortLeadGeo {
  city: string
  country: string
  ip: null | string
  postalCode: null | string
  region: null | string
  timezone: null | string
}

export interface ICreateCohortLeadRequest {
  cohort: string
  leadId: string
  geo: ICreateCohortLeadGeo
}

export interface ICreateLeadRequest {
  appName: string
  attribution?: {
    fbc: string | null
    fbp: string | null
    utmCampaign: string | null
    utmContent: string | null
    utmMedium: string | null
    utmSource: string | null
    utmTerm: string | null
  }
  cohort: string
  country?: string
  email: string | null
  extra?: Record<string, unknown>
  firebaseToken?: string | null
  locale: string
}

export interface IGetFirebaseCustomTokenResponse {
  customToken: string
}

export interface ILeadStepRaw {
  extra: Record<string, unknown>
  hasHeader: boolean
  hasProgressbar: boolean
  isPayment: boolean
  isQuestion: boolean
  isSkippable: boolean
  isSubscriptions: boolean
  isUpsell: boolean
  pageId: PageId
}

export const enum OnboardingType {
  PURCHASE = 'purchase',
  CANCEL_OFFER = 'cancelOffer',
  UPSELL = 'upsell',
  CHAT_UPSELL = 'chatUpsell',
}

export interface ILeadPurchaseItem {
  id: string
  paymentSystemName: string
  paymentSystemType: PaymentSystem
  planGroup?: string
  planId: string
  planOnboardingType: OnboardingType
  productName: string
}

export type TLeadSubscriptionStatus =
  | 'created'
  | 'trial'
  | 'active'
  | 'cancelled'
  | 'dunning'
  | 'trial_paused'
  | 'active_paused'
  | 'active_cancel_requested'
  | 'trial_cancel_requested'
  | 'active_resuming'
  | 'active_extended'

export interface ILeadSubscriptionItem {
  id: string
  stripeId?: string
  paypalId?: string
  status: TLeadSubscriptionStatus
  paymentSystemName: string
  paymentSystemType: PaymentSystem
  purchaseId: string
  planOnboardingType: OnboardingType
  planId: string
  planGroup?: string
  productName: string
  createdAt: string
}

export interface IPaymentConfigItem {
  id: string
  name: string
  isWeb: boolean
  type: PaymentSystem
  paypalClientId?: string
  stripeAccountId?: string
  stripeAccountName?: string
  stripePublicKey?: string
}

export interface ILeadRaw {
  appName: string
  cohort: Cohort | null
  cohortLeadId: string | null
  config: {
    app: {
      amplitudeApiKey: string
      firebaseConfig: firebase.remoteConfig.RemoteConfig | null
      googleAnalyticsId: string
      id: string
    }
    cohort: {
      facebookPixelIds: string[] | null
      giaApiKey: string
      id: string
      pinterestTagId: string
      snapPixelId: string
      steps: ILeadStepRaw[]
      tiktokPixelId: string
    }
    payment: IPaymentConfigItem[]
  }
  country: string | null
  email: string | null
  extra: Record<string, unknown>
  firebaseToken: string | null
  id: string
  locale: string
  quizzes: TAnswers
  purchases: ILeadPurchaseItem[] | null
  subscriptions: ILeadSubscriptionItem[] | null
}

export type TPlans = Record<OnboardingType, ILeadPlanRaw[]>

export interface IPlansResponseRaw {
  currency: Currency
  plans: TPlans
}

export interface IPlanPrice {
  periodInterval: number
  periodUnit: TimeInterval
  price: number
}

export interface IPaymentPrice {
  introPrice?: string
  paymentSystemName: string
  price: string
}

export interface ILeadPlanRaw {
  id: string
  name: string // decorations should be related to this name
  paypalPlanId: string
  paymentPrices?: IPaymentPrice[]
  prices: {
    intro: IPlanPrice
    main: IPlanPrice
  }
  product: string
  stripeIntroPriceId: string
  stripePriceId: string
  tags: string[] // needed for filtering (hidden plans before timer ends)
  onboardingType: OnboardingType // page name within the plan exists
  planGroup?: string // for linking purchases and cancel offers plans with chat upsell plans
  type: string
}
